import React, { useMemo } from 'react';
import { Button } from 'react-bootstrap';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAdvanceTableContext } from 'providers/AdvanceTableProvider';

import Flex from 'components/common/Flex';

export const TablePagination = () => {
  const {
    getState,
    getPageCount,
    getCanPreviousPage,
    getCanNextPage,
    options: { onPaginationChange },
  } = useAdvanceTableContext();

  const {
    pagination: { pageIndex, pageSize },
  } = getState();

  // Calculate total pages for server-side pagination
  const calculateTotalPages = () => {
    const state = getState();

    if (state.pagination?.pageSize && state.rowCount) {
      return Math.ceil(state.rowCount / state.pagination.pageSize);
    }
    return getPageCount();
  };

  const totalPages = calculateTotalPages();

  // 페이지 배열 생성
  const pageRange = useMemo(() => {
    const range = [];

    if (totalPages <= 7) {
      for (let i = 0; i < totalPages; i++) {
        range.push(i);
      }
    } else {
      const leftBound = Math.max(0, pageIndex - 2);
      const rightBound = Math.min(totalPages - 1, pageIndex + 2);

      if (leftBound > 0) {
        range.push(0);
        if (leftBound > 1) {
          range.push('...');
        }
      }

      for (let i = leftBound; i <= rightBound; i++) {
        range.push(i);
      }

      if (rightBound < totalPages - 1) {
        if (rightBound < totalPages - 2) {
          range.push('...');
        }
        range.push(totalPages - 1);
      }
    }

    return range;
  }, [totalPages, pageIndex]);

  const handlePageChange = newPage => {
    if (pageIndex === newPage) {
      return;
    }
    onPaginationChange?.({ pageIndex: newPage, pageSize });
  };

  const handlePrevious = () => {
    if (getCanPreviousPage()) {
      onPaginationChange?.({ pageIndex: pageIndex - 1, pageSize });
    }
  };

  const handleNext = () => {
    if (getCanNextPage()) {
      onPaginationChange?.({ pageIndex: pageIndex + 1, pageSize });
    }
  };

  return (
    <Flex alignItems="center" justifyContent="center">
      <Button
        size="sm"
        variant="falcon-default"
        onClick={handlePrevious}
        className={classNames({ disabled: !getCanPreviousPage() })}
      >
        <FontAwesomeIcon icon="chevron-left" />
      </Button>
      <ul className="pagination mb-0 mx-2">
        {pageRange.map((page, index) => {
          if (page === '...') {
            return (
              <li key={`ellipsis-${index}`} className="px-2">
                ...
              </li>
            );
          }
          return (
            <li
              key={`page-${page}`}
              className={classNames({
                active: pageIndex === page,
              })}
            >
              <Button
                size="sm"
                variant="falcon-default"
                className={classNames('page', {
                  'me-2': index + 1 !== pageRange.length,
                })}
                onClick={() => handlePageChange(page)}
              >
                {page + 1}
              </Button>
            </li>
          );
        })}
      </ul>
      <Button
        size="sm"
        variant="falcon-default"
        onClick={handleNext}
        className={classNames({ disabled: !getCanNextPage() })}
      >
        <FontAwesomeIcon icon="chevron-right" />
      </Button>
    </Flex>
  );
};

export default TablePagination;
